/**
 * BracketMatchup - A single game matchup showing two teams.
 *
 * Fixed-height card (MATCHUP_H in BracketRegion) with two team rows,
 * a prediction strip (projected scores, O/U edge, win probability),
 * and a compact footer with Analyze, Knewton, and Battle buttons.
 * Height is consistent regardless of state so connector lines always align.
 */

import BracketTeam from './BracketTeam';

const LockIcon = () => (
  <svg className="w-2.5 h-2.5 text-yellow-500/70" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
  </svg>
);

function BracketMatchup({
  pick,
  entriesMap,
  prediction,
  onPickWinner,
  onAnalyze,
  onAskKnewton,
  onMascotBattle,
  onPremiumGate,
  isPremium = false,
  roundNumber,
  gameSlot,
  readOnly = false,
}) {
  // Look up from entriesMap first (tournament field), fall back to nested pick data
  const team1 = pick?.team1_entry_id
    ? (entriesMap[pick.team1_entry_id] || pick.team1 || null)
    : null;
  const team2 = pick?.team2_entry_id
    ? (entriesMap[pick.team2_entry_id] || pick.team2 || null)
    : null;
  const winnerId = pick?.winner_entry_id;

  const hasBothTeams = team1 && team2;
  const isUpset = pick?.is_upset === 1;

  // Prediction data
  const pred = hasBothTeams ? prediction : null;
  const ouEdge = pred?.over_under_edge;
  const ouLine = pred?.over_under_line;
  const hasOU = ouEdge != null && ouLine != null;
  const isOver = ouEdge > 0;
  const winPct = pred ? Math.round(pred.win_probability * 100) : null;

  // Game result data
  const isFinal = pred?.is_final === true;
  const actualScoreT1 = pred?.actual_score_team1;
  const actualScoreT2 = pred?.actual_score_team2;
  const actualWinnerId = pred?.actual_winner_entry_id;
  const userPickCorrect = isFinal && winnerId && actualWinnerId && winnerId === actualWinnerId;
  const userPickWrong = isFinal && winnerId && actualWinnerId && winnerId !== actualWinnerId;
  const gameLocked = isFinal || readOnly;

  const handlePick = (entry) => {
    if (gameLocked || !hasBothTeams) return;
    onPickWinner?.(roundNumber, gameSlot, entry.id);
  };

  const handleAnalyze = (e) => {
    e.stopPropagation();
    if (!hasBothTeams) return;
    onAnalyze?.(team1.id, team2.id, roundNumber);
  };

  const handlePremiumAction = (action) => (e) => {
    e.stopPropagation();
    if (!hasBothTeams) return;
    if (!isPremium) {
      onPremiumGate?.();
      return;
    }
    action();
  };

  return (
    <div className={`
      relative w-52 rounded-lg border overflow-hidden transition-all duration-200
      ${isFinal
        ? userPickCorrect
          ? 'bg-emerald-500/5 border-emerald-500/40'
          : userPickWrong
            ? 'bg-red-500/5 border-red-500/30'
            : 'bg-bg-card/60 border-border/40'
        : hasBothTeams && !readOnly
          ? 'bg-bg-card/80 border-border/50 hover:border-accent-blue/40 hover:shadow-lg hover:shadow-accent-blue/5'
          : 'bg-bg-card/40 border-border/30'
      }
    `}>
      {/* Status badge */}
      {isFinal ? (
        <div className="absolute -top-0.5 -right-0.5 z-10">
          {userPickCorrect ? (
            <span className="px-1.5 py-0.5 text-[9px] font-bold bg-emerald-500 text-white rounded-bl-lg rounded-tr-lg shadow-sm">
              CORRECT
            </span>
          ) : userPickWrong ? (
            <span className="px-1.5 py-0.5 text-[9px] font-bold bg-red-500 text-white rounded-bl-lg rounded-tr-lg shadow-sm">
              WRONG
            </span>
          ) : (
            <span className="px-1.5 py-0.5 text-[9px] font-bold bg-text-tertiary text-white rounded-bl-lg rounded-tr-lg shadow-sm">
              FINAL
            </span>
          )}
        </div>
      ) : isUpset && (
        <div className="absolute -top-0.5 -right-0.5 z-10">
          <span className="px-1.5 py-0.5 text-[9px] font-bold bg-gradient-to-r from-danger to-orange-500 text-white rounded-bl-lg rounded-tr-lg shadow-sm">
            UPSET
          </span>
        </div>
      )}

      {/* Teams */}
      <div className="divide-y divide-border/20">
        <BracketTeam
          entry={team1}
          isWinner={isFinal ? actualWinnerId === team1?.id : winnerId === team1?.id}
          isEliminated={isFinal ? actualWinnerId && actualWinnerId !== team1?.id : winnerId && winnerId !== team1?.id}
          projectedScore={isFinal ? actualScoreT1 : pred?.projected_score_team1}
          isFavored={isFinal ? actualWinnerId === team1?.id : pred && pred.winner_entry_id === team1?.id}
          onClick={gameLocked ? null : handlePick}
          isFinal={isFinal}
        />
        <BracketTeam
          entry={team2}
          isWinner={isFinal ? actualWinnerId === team2?.id : winnerId === team2?.id}
          isEliminated={isFinal ? actualWinnerId && actualWinnerId !== team2?.id : winnerId && winnerId !== team2?.id}
          projectedScore={isFinal ? actualScoreT2 : pred?.projected_score_team2}
          isFavored={isFinal ? actualWinnerId === team2?.id : pred && pred.winner_entry_id === team2?.id}
          onClick={gameLocked ? null : handlePick}
          isFinal={isFinal}
        />
      </div>

      {/* Prediction strip: win probability bar + O/U edge */}
      <div className="flex items-center border-t border-border/20 h-4 px-1 gap-1 bg-bg-primary/30">
        {pred ? (
          <>
            {/* Win probability bar */}
            <div className="flex-1 flex items-center gap-1">
              <div className="flex-1 h-1.5 rounded-full bg-bg-card-hover overflow-hidden">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-accent-blue to-accent-purple transition-all duration-300"
                  style={{ width: `${winPct}%` }}
                />
              </div>
              <span className="text-[8px] font-bold text-text-tertiary w-7 text-right tabular-nums">
                {winPct}%
              </span>
            </div>

            {/* O/U edge pill */}
            {hasOU ? (
              <span className={`text-[8px] font-bold px-1 py-0.5 rounded ${
                isOver
                  ? 'text-emerald-400 bg-emerald-500/10'
                  : 'text-red-400 bg-red-500/10'
              }`}>
                {isOver ? 'O' : 'U'} {Math.abs(ouEdge).toFixed(1)}
              </span>
            ) : (
              <span className="text-[8px] font-bold px-1 py-0.5 rounded text-text-tertiary/40 tabular-nums">
                {pred.predicted_total}
              </span>
            )}
          </>
        ) : (
          <div className="flex-1 h-1.5 rounded-full bg-bg-card-hover/50" />
        )}
      </div>

      {/* Footer: Analyze + Knewton + Battle */}
      <div className="flex items-center border-t border-border/30 h-5">
        {hasBothTeams && onAnalyze && (
          <>
            {/* Analyze - always free */}
            <button
              onClick={handleAnalyze}
              className="flex-1 text-[10px] font-medium text-text-tertiary hover:text-accent-blue hover:bg-accent-blue/5 transition-all flex items-center justify-center gap-1 h-full"
            >
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5" />
              </svg>
              Analyze
            </button>

            {/* Knewton - premium */}
            <div className="w-px h-3 bg-border/30" />
            <button
              onClick={handlePremiumAction(() => onAskKnewton?.(team1, team2, roundNumber))}
              className={`flex-1 text-[10px] font-medium transition-all flex items-center justify-center gap-1 h-full ${
                isPremium
                  ? 'text-purple-400/70 hover:text-purple-300 hover:bg-purple-500/5'
                  : 'text-text-tertiary/40 hover:text-text-tertiary'
              }`}
            >
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
              </svg>
              Knewton
              {!isPremium && <LockIcon />}
            </button>

            {/* Mascot Battle - premium */}
            <div className="w-px h-3 bg-border/30" />
            <button
              onClick={handlePremiumAction(() => onMascotBattle?.(team1.id, team2.id))}
              className={`flex-1 text-[10px] font-medium transition-all flex items-center justify-center gap-1 h-full ${
                isPremium
                  ? 'text-red-400/70 hover:text-red-300 hover:bg-red-500/5'
                  : 'text-text-tertiary/40 hover:text-text-tertiary'
              }`}
            >
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.362 5.214A8.252 8.252 0 0112 21 8.25 8.25 0 016.038 7.048 8.287 8.287 0 009 9.6a8.983 8.983 0 013.361-6.867 8.21 8.21 0 003 2.48z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 18a3.75 3.75 0 00.495-7.467 5.99 5.99 0 00-1.925 3.546 5.974 5.974 0 01-2.133-1A3.75 3.75 0 0012 18z" />
              </svg>
              Battle
              {!isPremium && <LockIcon />}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default BracketMatchup;
